import { CompassRose, RhumbLines, ScaleOfMiles, SignalBunting } from '@brig/theme/ornaments';
import { button, visuallyHidden } from '@brig/theme/styles';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet } from 'react-router';
import { useMe, useUpdateMe } from '../api/hooks.ts';
import { useAuth } from '../auth/useAuth.ts';
import i18n, { currentLocale } from '../i18n/index.ts';
import * as styles from './AppShell.css.ts';
import { GridLabels } from './GridLabels.tsx';
import { LanguageSwitcher } from './LanguageSwitcher.tsx';
import { NewGameButton } from './NewGameButton.tsx';

/** Once the profile loads, its saved language wins over browser detection. */
function useProfileLanguage() {
  const { data: me } = useMe();
  useEffect(() => {
    if (me && me.locale !== currentLocale()) void i18n.changeLanguage(me.locale);
  }, [me]);
}

export function AppShell() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { data: me } = useMe();
  const updateMe = useUpdateMe();
  useProfileLanguage();

  return (
    <div className={styles.chart}>
      <RhumbLines originX={0.92} originY={0.08} />
      <GridLabels />
      <a href="#main" className={styles.skipLink}>
        {t('app.skipToContent')}
      </a>

      <header className={styles.header}>
        <Link to="/harbour" className={styles.brand}>
          <CompassRose size={44} northLabel="" ink="#2F2418" paper="#F1E6C8" accent="#A8863F" />
          <span className={styles.brandText}>
            <span className={styles.brandName}>{t('app.shortName')}</span>
            <span className={styles.brandSub}>{t('app.subtitle')}</span>
          </span>
        </Link>

        <nav aria-label={t('nav.label')} className={styles.nav}>
          <NavLink to="/harbour" className={styles.navLink}>
            {t('nav.harbour')}
          </NavLink>
          <NavLink to="/logbook" className={styles.navLink}>
            {t('nav.logbook')}
          </NavLink>
        </nav>

        <div className={styles.actions}>
          <NavLink to="/ships" className={styles.navLink}>
            {t('shipsPage.title')}
          </NavLink>
          <NavLink to="/how-to-play" className={styles.navLink}>
            {t('learn.open')}
          </NavLink>
          <NewGameButton size="sm" />
          <LanguageSwitcher
            onChange={(locale) => {
              if (me && me.locale !== locale) updateMe.mutate({ locale });
            }}
          />
          <div className={styles.account}>
            {me ? (
              <NavLink
                to="/profile"
                className={`${button({ tone: 'label', size: 'sm' })} ${styles.who}`}
                title={t('nav.profile')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <circle cx="8" cy="5" r="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M2.5 14.5c.6-3 2.8-4.5 5.5-4.5s4.9 1.5 5.5 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span className={styles.nick}>{me.nickname}</span>
                <span className={visuallyHidden}> — {t('nav.profile')}</span>
              </NavLink>
            ) : null}
            <button
              type="button"
              className={button({ tone: 'ghost', size: 'sm' })}
              onClick={() => void signOut()}
            >
              {t('nav.signOut')}
              {me ? <span className={visuallyHidden}> ({me.nickname})</span> : null}
            </button>
          </div>
        </div>
      </header>

      <main id="main" tabIndex={-1} className={styles.main}>
        <Outlet />
      </main>

      <footer className={styles.footer}>
        <ScaleOfMiles caption={t('footer.scale')} color="#2F2418" fill="#F1E6C8" />
        <SignalBunting color="#2F2418" />
        <p className={styles.imprint}>
          {t('footer.imprint')} · <Link to="/credits">{t('credits.link')}</Link>
        </p>
      </footer>
    </div>
  );
}
